/**
 * One entry, in full (§1.4, BR-06).
 *
 * Opened from the transaction list by any signed-in user. Read transparency is
 * the point of the system: every partner can see what was recorded, the
 * receipt behind it, and — if it was rejected, voided or amended — why.
 */

import { useEffect, type ReactNode } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatMoney } from '../../lib/currency.js';
import { formatDate } from '../../lib/date.js';
import type { Transaction } from './api.js';
import { ReceiptViewer } from './ReceiptViewer.js';
import { StatusBadge } from './StatusBadge.js';

interface TransactionDetailProps {
  txn: Transaction;
  categoryName: string;
  onClose: () => void;
  /** Extra controls under the details — the admin's edit and void (P4-T32). */
  actions?: ReactNode;
}

function Row({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 py-2 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right text-gray-900">{children}</dd>
    </div>
  );
}

export function TransactionDetail({ txn, categoryName, onClose, actions }: TransactionDetailProps): JSX.Element {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // The receipt viewer inside handles its own Escape; this one closes the
      // detail only when nothing is stacked on top of it.
      if (event.key === 'Escape' && document.querySelectorAll('[role="dialog"]').length === 1) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('finance.detail.title')}
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 md:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-4 shadow-xl md:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h2 className="truncate text-lg font-semibold text-gray-900">{categoryName}</h2>
          <span className="shrink-0 text-xl font-semibold tabular-nums text-gray-900">{formatMoney(txn.amount)}</span>
        </div>

        {txn.amendReason !== null && (
          <p className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t('finance.detail.amended', { reason: txn.amendReason })}
          </p>
        )}

        <dl>
          <Row label={t('finance.detail.type')}>{t(`finance.type.${txn.type}`)}</Row>
          <Row label={t('finance.detail.date')}>
            {formatDate(txn.txnDate)}
            {txn.isBackdated && ` · ${t('finance.backdated')}`}
          </Row>
          <Row label={t('finance.detail.status')}>
            <StatusBadge status={txn.status} />
          </Row>
          {txn.note !== null && <Row label={t('finance.detail.note')}>{txn.note}</Row>}
          {txn.rejectReason !== null && <Row label={t('finance.detail.rejectReason')}>{txn.rejectReason}</Row>}
          {txn.voidReason !== null && <Row label={t('finance.detail.voidReason')}>{txn.voidReason}</Row>}
        </dl>

        <div className="mt-3">
          <p className="text-sm text-gray-500">{t('finance.detail.receipt')}</p>
          {txn.receiptUrl !== null ? (
            <ReceiptViewer uploadId={txn.receiptUrl} />
          ) : (
            <p className="mt-1 text-sm text-gray-400">{t('finance.receipt.none')}</p>
          )}
        </div>

        {actions}

        <button
          type="button"
          onClick={onClose}
          className="mt-4 min-h-touch w-full rounded-lg bg-gray-100 px-3 py-2 text-gray-700"
        >
          {t('finance.detail.close')}
        </button>
      </div>
    </div>
  );
}
